// ./src/components/features/Alerts/AlertList/AlertList.tsx
import React from "react";
import { Stack, Text, Paper, Group, Button, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { UserNotification } from "@/utils/api/UserNotificationService";

import { AppIcon } from "@/components/generic/AppIcon";

import { AlertItem } from "../AlertItem";

// ============================================
// Types
// ============================================

export interface AlertListProps {
  /** Array of notifications to display */
  notifications: UserNotification[];

  /** Loading state */
  loading?: boolean;

  /** Maximum number of items to show (0 = show all) */
  maxItems?: number;

  /** Handler for clicking an alert */
  onAlertClick?: (notification: UserNotification) => void;

  /** Handler for "View All" button */
  onViewAll?: () => void;

  /** Handler for "Mark All Read" button */
  onMarkAllRead?: () => void;

  /** Whether to show timestamps */
  showTimestamps?: boolean;

  /** Size variant */
  size?: "sm" | "md";

  /** Empty state message */
  emptyMessage?: string;

  /** Empty state icon */
  emptyIcon?: string;

  /** Title for the list */
  title?: string;

  /** Whether to show the header */
  showHeader?: boolean;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const SKELETON_COUNT = 3;

// ============================================
// Sub-components
// ============================================

const AlertListSkeleton: React.FC<{ count: number; size: "sm" | "md" }> = ({
  count,
  size,
}) => {
  const height = size === "sm" ? 60 : 72;

  return (
    <Stack gap="xs">
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} height={height} radius="md" />
      ))}
    </Stack>
  );
};

const AlertListEmpty: React.FC<{ message: string; iconName: string }> = ({
  message,
  iconName,
}) => (
  <Stack align="center" gap="sm" py="xl">
    <AppIcon
      iconName={iconName}
      size={48}
      color="var(--mantine-color-gray-4)"
    />
    <Text size="sm" c="dimmed" ta="center">
      {message}
    </Text>
  </Stack>
);

// ============================================
// Component
// ============================================

/**
 * Displays a list of alerts/notifications.
 * Includes header with title and action buttons, loading and empty states.
 */
export const AlertList: React.FC<AlertListProps> = ({
  notifications,
  loading = false,
  maxItems = 0,
  onAlertClick,
  onViewAll,
  onMarkAllRead,
  showTimestamps = true,
  size = "md",
  emptyMessage,
  emptyIcon = "BellOff",
  title,
  showHeader = true,
  className,
}) => {
  const { t } = useTranslation();

  // Use translations for default values
  const displayTitle = title ?? t("alerts.title");
  const displayEmptyMessage = emptyMessage ?? t("alerts.empty");

  // Determine which notifications to display
  const displayNotifications =
    maxItems > 0 ? notifications.slice(0, maxItems) : notifications;

  // Count unread notifications
  const unreadCount = notifications.filter(
    (n) => !(n as any).isRead && !(n as any).readAt
  ).length;

  const hasMore = maxItems > 0 && notifications.length > maxItems;

  return (
    <Paper radius="md" className={className}>
      {/* Header */}
      {showHeader && (
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <Text fw={600} size="lg">
              {displayTitle}
            </Text>
            {unreadCount > 0 && (
              <Text size="sm" c="dimmed">
                {t("alerts.newCount", { count: unreadCount })}
              </Text>
            )}
          </Group>

          {onMarkAllRead && unreadCount > 0 && (
            <Button
              variant="subtle"
              size="xs"
              onClick={onMarkAllRead}
              leftSection={<AppIcon iconName="Checks" size={14} />}
            >
              {t("alerts.markAllRead")}
            </Button>
          )}
        </Group>
      )}

      {/* Content */}
      {loading ? (
        <AlertListSkeleton count={SKELETON_COUNT} size={size} />
      ) : displayNotifications.length === 0 ? (
        <AlertListEmpty message={displayEmptyMessage} iconName={emptyIcon} />
      ) : (
        <Stack gap="xs">
          {displayNotifications.map((notification, index) => (
            <AlertItem
              key={(notification as any).id || index}
              notification={notification}
              onClick={onAlertClick}
              showTimestamp={showTimestamps}
              size={size}
            />
          ))}
        </Stack>
      )}

      {/* View All Button */}
      {hasMore && onViewAll && (
        <Button
          variant="subtle"
          fullWidth
          mt="md"
          onClick={onViewAll}
          rightSection={<AppIcon iconName="ChevronRight" size={16} />}
        >
          {t("alerts.viewAll", { count: notifications.length })}
        </Button>
      )}
    </Paper>
  );
};
